"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, SlidersHorizontal as Sliders, Shield, Wind, Zap, Maximize, TriangleAlert as AlertTriangle, FileCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
interface SpecItem {
  id: string;
  code: string;
  title: string;
  description: string;
  iconName: string;
}
const specs: SpecItem[] = [{
  id: "spec-1",
  code: "SYS-01",
  title: "Bull & Motion Base",
  description: "The central powered equipment.",
  iconName: "Activity"
}, {
  id: "spec-2",
  code: "SYS-02",
  title: "Operator Controller",
  description: "The control system supplied for the machine.",
  iconName: "Sliders"
}, {
  id: "spec-3",
  code: "SYS-03",
  title: "Inflatable Safety Arena",
  description: "The surrounding inflatable area where applicable.",
  iconName: "Shield"
}, {
  id: "spec-4",
  code: "SYS-04",
  title: "Blower",
  description: "Used to maintain inflation where required.",
  iconName: "Wind"
}, {
  id: "spec-5",
  code: "SYS-05",
  title: "Suitable Power",
  description: "Confirm the requirements for the exact bull, controller and blower.",
  iconName: "Zap"
}, {
  id: "spec-6",
  code: "SYS-06",
  title: "Clear Operating Area",
  description: "Plan around the complete system rather than the machine base alone.",
  iconName: "Maximize"
}];
function renderIcon(iconName: string) {
  switch (iconName) {
    case "Activity":
      return <Activity className="size-4 text-primary" />;
    case "Sliders":
      return <Sliders className="size-4 text-primary" />;
    case "Shield":
      return <Shield className="size-4 text-primary" />;
    case "Wind":
      return <Wind className="size-4 text-primary" />;
    case "Zap":
      return <Zap className="size-4 text-primary" />;
    case "Maximize":
      return <Maximize className="size-4 text-primary" />;
    default:
      return <Activity className="size-4 text-primary" />;
  }
}
export default function Setuprequirements() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="setuprequirements" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="max-w-3xl">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 bg-primary" />
            <span className="font-mono text-xs font-semibold tracking-widest text-muted-foreground uppercase">
              Equipment Specifications &amp; Prerequisites
            </span>
          </div>

          <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            What Do You Need to Set Up a Mechanical Bull?
          </h2>

          {/* Executive Direct-Answer Summary Box */}
          <div className="mt-6 border-l-2 border-primary bg-muted/60 p-5 sm:p-6">
            <p className="text-base leading-relaxed text-foreground md:text-lg">
              A complete mechanical bull setup may include the bull and motion base, operator
              controller, inflatable safety arena, blower, cables and other model-specific
              accessories. You also need enough space for the complete operating area, suitable site
              access and the power required by the exact equipment.
            </p>
          </div>
        </motion.div>

        {/* 6-Card Symmetrical Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} variants={containerVariants} className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {specs.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="flex">
              <Card className="flex w-full flex-col justify-between rounded-none border border-border bg-card p-0 shadow-xs transition-colors duration-200 hover:border-foreground">
                <CardHeader className="gap-4 border-b border-border/60 p-6 pb-4">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground">
                      {item.code}
                    </span>
                    <div className="flex size-7 items-center justify-center border border-border bg-muted/40">
                      {renderIcon(item.iconName)}
                    </div>
                  </div>
                  <CardTitle className="font-serif text-xl font-bold tracking-tight text-card-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-4">
                  <CardDescription className="text-sm leading-normal text-muted-foreground">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Industrial Rating Plate Container / Closing Note */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mt-10 border border-border bg-card p-6 shadow-xs sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3.5">
              <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center border border-border bg-muted">
                <AlertTriangle className="size-4 text-primary" />
              </div>
              <div>
                <p className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  Installation Notice
                </p>
                <p className="mt-1 text-sm font-medium leading-relaxed text-foreground">
                  Never assume that every mechanical bull uses the same setup. Check the individual
                  product and manufacturer information before installation.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 border-t border-border pt-3 font-mono text-xs text-muted-foreground sm:border-t-0 sm:pt-0">
              <FileCheck className="size-4 text-muted-foreground" />
              <span>COMMERCIAL SPEC</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}